/**
 * Analytics utility functions for Simple Analytics event tracking
 * https://docs.simpleanalytics.com/events
 */

import posthog from '../posthog'

export const SIGN_UP_SOURCE_KEY = 'signup_source';

const setSignUpSource = (source) => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SIGN_UP_SOURCE_KEY, source);
    }
  } catch (_) { }
};

export const consumeSignUpSource = () => {
  try {
    if (typeof sessionStorage !== 'undefined') {
      const val = sessionStorage.getItem(SIGN_UP_SOURCE_KEY);
      sessionStorage.removeItem(SIGN_UP_SOURCE_KEY);
      return val;
    }
  } catch (_) { }
  return null;
};

/**
 * Track an event with Simple Analytics
 * @param {string} eventName - The name of the event to track
 * @param {Object} metadata - Optional metadata to include with the event
 */
export const trackEvent = (eventName, metadata = {}) => {
  try {
    // Check if Simple Analytics is loaded
    if (typeof window !== 'undefined' && typeof window.sa_event === 'function') {
      window.sa_event(eventName, metadata);
    } else {
      // Fallback for development or when SA is not loaded
      console.log('Analytics event:', eventName, metadata);
    }
  } catch (error) {
    console.warn('Failed to track analytics event:', error);
  }
};

// Specific event tracking functions for different user actions

/**
 * Track sign-up button clicks
 * @param {string} location - Where the sign-up button was clicked from
 */
export const trackSignUpClick = (location) => {
  setSignUpSource(location || 'unknown');
  trackEvent('signup_click', { location });
};

/**
 * Track file upload and compression events
 * @param {Object} options - Upload details
 * @param {boolean} options.isSignedIn - Whether user is signed in
 * @param {number} options.fileSizeMB - File size in MB
 * @param {string} options.fileType - File extension or type
 * @param {string} options.compressionRatio - Compression ratio achieved (optional)
 */
export const trackFileUpload = ({ isSignedIn, fileSizeMB, fileType, compressionRatio }) => {
  const eventName = isSignedIn ? 'file_upload_signed_in' : 'file_upload_anonymous';
  trackEvent(eventName, {
    file_size_mb: fileSizeMB,
    file_type: fileType,
    compression_ratio: compressionRatio
  });
};

/**
 * Track when sign-up dialogs are triggered due to limits
 * @param {string} reason - Why the dialog was shown ('file-size' or 'rate-limit')
 * @param {number} fileSizeMB - File size in MB (if applicable)
 */
export const trackSignUpDialogTriggered = (reason, fileSizeMB = null) => {
  const metadata = { reason };
  if (fileSizeMB !== null) {
    metadata.file_size_mb = fileSizeMB;
  }
  trackEvent('signup_dialog_triggered', metadata);
};

/**
 * Track sign-up button clicks from dialogs
 * @param {string} reason - The reason the dialog was shown ('file-size' or 'rate-limit')
 */
export const trackSignUpDialogClick = (reason) => {
  const source = reason ? `dialog-${reason}` : 'dialog';
  setSignUpSource(source);
  trackEvent('signup_dialog_click', { reason });
};

/**
 * Track billing package clicks
 * @param {Object} options - Billing package details
 * @param {number} options.price - Package price
 * @param {number} options.credits - Package credits
 * @param {string} options.package_name - Package name
 */
export const trackBillingPackageClick = ({ price, credits, package_name }) => {
  posthog.capture('billing_package_click', { price, credits, package_name });
};

/**
 * Track preset creation
 */
export const trackPresetCreate = () => {
  posthog.capture('preset_created');
};

/**
 * Track API key creation
 */
export function trackApiKeyCreated() {
  posthog.capture('api_key_created');
}

/**
 * Track when a compression job batch is started
 * @param {number} fileCount - Number of files in the batch
 * @param {number} totalSize - Total size of files in bytes
 * @param {object} options - Compression options used
 */
export function trackCompressionStarted(fileCount, totalSize, options) {
  posthog.capture('compression_started', {
    file_count: fileCount,
    total_size_bytes: totalSize,
    total_size_mb: (totalSize / 1024 / 1024).toFixed(2),
    has_custom_options: options ? true : false,
  });
}

/**
 * Track when a compression job succeeds
 * @param {string} jobId - Job ID
 * @param {number} originalSize - Original file size in bytes
 * @param {number} compressedSize - Compressed file size in bytes
 * @param {number} compressionRatio - Compression ratio (0-1)
 */
export function trackCompressionSuccess(jobId, originalSize, compressedSize, compressionRatio) {
  posthog.capture('compression_success', {
    job_id: jobId,
    original_size_bytes: originalSize,
    compressed_size_bytes: compressedSize,
    original_size_mb: (originalSize / 1024 / 1024).toFixed(2),
    compressed_size_mb: (compressedSize / 1024 / 1024).toFixed(2),
    compression_ratio: compressionRatio ? compressionRatio.toFixed(3) : null,
    size_reduction_percent: compressionRatio ? ((1 - compressionRatio) * 100).toFixed(1) : null,
  });
}

/**
 * Track when a compression job fails
 * @param {string} jobId - Job ID
 * @param {string} error - Error message
 */
export function trackCompressionFailed(jobId, error) {
  posthog.capture('compression_failed', {
    job_id: jobId,
    error_message: error,
  });
}